import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { List } from 'lucide-react'
import { cn } from '@/lib/utils'

type HeadingLink = {
  id: string
  text: string
}

const BLOG_ARTICLE_PATH_PATTERN = /^\/blog\/[^/]+\/?$/

const slugifyHeading = (value: string) => {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return slug || 'section'
}

const getScrollProgress = () => {
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight
  if (maxScroll <= 0) return 0
  return Math.min(1, Math.max(0, window.scrollY / maxScroll))
}

const resolveHeadings = () => {
  const main = document.querySelector('main')
  if (!main) return []

  const usedIds = new Set<string>()
  const headingNodes = Array.from(main.querySelectorAll<HTMLElement>('h2'))

  return headingNodes.map((heading, index) => {
    const text = heading.textContent?.trim() || `Section ${index + 1}`
    const requestedId = heading.id || slugifyHeading(text)
    let id = requestedId
    let suffix = 2

    while (usedIds.has(id)) {
      id = `${requestedId}-${suffix}`
      suffix += 1
    }

    usedIds.add(id)
    heading.id = id

    return { id, text }
  })
}

const ArticleNavigator = () => {
  const { pathname } = useLocation()
  const isBlogArticle = useMemo(
    () => BLOG_ARTICLE_PATH_PATTERN.test(pathname),
    [pathname]
  )

  const [headings, setHeadings] = useState<HeadingLink[]>([])
  const [activeHeadingId, setActiveHeadingId] = useState('')
  const [scrollProgress, setScrollProgress] = useState(0)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!isBlogArticle) {
      setHeadings([])
      setActiveHeadingId('')
      setScrollProgress(0)
      return
    }

    const frame = window.requestAnimationFrame(() => {
      const nextHeadings = resolveHeadings()
      setHeadings(nextHeadings)
      setActiveHeadingId(nextHeadings[0]?.id ?? '')
    })

    return () => window.cancelAnimationFrame(frame)
  }, [isBlogArticle, pathname])

  useEffect(() => {
    if (!isBlogArticle || headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top)

        if (visible.length > 0) {
          setActiveHeadingId(visible[0].target.id)
        }
      },
      {
        root: null,
        threshold: [0, 1],
        rootMargin: '-32% 0px -55% 0px'
      }
    )

    headings.forEach(({ id }) => {
      const heading = document.getElementById(id)
      if (heading) observer.observe(heading)
    })

    return () => observer.disconnect()
  }, [headings, isBlogArticle])

  useEffect(() => {
    if (!isBlogArticle) return

    const handleScroll = () => setScrollProgress(getScrollProgress())
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [isBlogArticle, pathname])

  if (!isBlogArticle) return null

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-40 h-0.5 bg-foreground/10">
        <div
          className="h-full bg-foreground/50 transition-[width] duration-150 ease-out"
          style={{ width: `${Math.round(scrollProgress * 100)}%` }}
        />
      </div>

      {headings.length > 1 && (
        <>
          <aside className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-30">
            <nav className="w-56 rounded-lg border border-foreground/15 bg-[#ddd]/90 backdrop-blur px-3 py-3">
              <p className="text-[0.625rem] uppercase tracking-[0.18em] text-foreground/60">
                On this page
              </p>
              <ul className="mt-2 space-y-1.5">
                {headings.map((heading) => (
                  <li key={heading.id}>
                    <a
                      href={`#${heading.id}`}
                      className={cn(
                        'block text-[0.78rem] leading-snug transition-colors hover:text-foreground',
                        activeHeadingId === heading.id
                          ? 'text-foreground font-medium'
                          : 'text-foreground/55'
                      )}
                    >
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <div className="xl:hidden fixed bottom-4 right-4 z-40">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 bg-[#ddd] px-3 py-2 text-sm font-medium text-foreground shadow-sm"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle section navigation"
            >
              <List className="h-4 w-4" />
              Sections
            </button>

            {isMobileMenuOpen && (
              <nav className="absolute bottom-12 right-0 w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-foreground/15 bg-[#ddd] p-3 shadow-lg">
                <p className="text-[0.625rem] uppercase tracking-[0.16em] text-foreground/60">
                  Jump to
                </p>
                <ul className="mt-2 space-y-2 max-h-56 overflow-auto pr-1">
                  {headings.map((heading) => (
                    <li key={heading.id}>
                      <a
                        href={`#${heading.id}`}
                        className={cn(
                          'block text-sm leading-snug transition-colors hover:text-foreground',
                          activeHeadingId === heading.id
                            ? 'text-foreground font-medium'
                            : 'text-foreground/65'
                        )}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </>
      )}
    </>
  )
}

export default ArticleNavigator
